import { SitePage } from '@web-studio/core-components';
import type {
  CornerEnumKey,
  EditTarget,
  LayoutBlock,
  PageMeta,
  SiteMode,
  SpacingEnumKey,
  TypographyEnumKey,
} from '@web-studio/shared-schemas';
import { isGlobalFloatComponentType } from '@web-studio/shared-schemas';
import type { MatrixLocale } from '@web-studio/core-fixtures';
import { forwardRef } from 'react';

interface MatrixCanvasProps {
  blocks: LayoutBlock[];
  locale: MatrixLocale;
  themeId: string;
  colorsetId: string;
  spacingEnum: SpacingEnumKey;
  cornerEnum?: CornerEnumKey;
  typographyEnum?: TypographyEnumKey;
  viewport: 'desktop' | 'tablet' | 'mobile';
  compact?: boolean;
  editable?: boolean;
  showEditTargets?: boolean;
  /** Keep block selection band even when a field child is active (Engineer). */
  keepBlockBand?: boolean;
  activeBlockId?: string | null;
  activeEditTarget?: EditTarget | null;
  onBlockSelect?: (blockId: string, editTarget?: EditTarget) => void;
  /** Nav IA — match sandbox / project bootstrap (marketplace capture). */
  pages?: PageMeta[];
  defaultLocale?: string;
  supportedLocales?: string[];
  siteMode?: SiteMode;
  vertical?: string;
  /** Marks the current nav link. Home compositions default to `/`. */
  pathname?: string | null;
}

export const MatrixCanvas = forwardRef<HTMLDivElement, MatrixCanvasProps>(function MatrixCanvas(
  {
    blocks,
    locale,
    themeId,
    colorsetId,
    spacingEnum,
    cornerEnum,
    typographyEnum,
    viewport,
    compact = false,
    editable = false,
    showEditTargets = false,
    keepBlockBand = false,
    activeBlockId = null,
    activeEditTarget = null,
    onBlockSelect,
    pages,
    defaultLocale,
    supportedLocales,
    siteMode,
    vertical,
    pathname = '/',
  },
  ref,
) {
  /** Float-only isolate has no page height — site min-height lets scroll-reveal work. */
  const floatOnly =
    blocks.length > 0 && blocks.every((block) => isGlobalFloatComponentType(block.componentType));

  const frameClass = [
    'lab-canvas-frame',
    viewport === 'mobile' ? 'lab-canvas-frame--phone' : '',
    viewport === 'tablet' ? 'lab-canvas-frame--tablet' : '',
    editable ? 'lab-canvas-frame--editable' : '',
    showEditTargets ? 'lab-canvas-frame--edit-targets' : '',
    keepBlockBand ? 'lab-canvas-frame--keep-band' : '',
    floatOnly ? 'lab-canvas-frame--float-chrome' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div ref={ref} className={frameClass} style={compact ? { minHeight: 200 } : { minHeight: 480 }}>
      <SitePage
        key={`${themeId}:${colorsetId || 'default'}:${spacingEnum}:${cornerEnum ?? ''}:${typographyEnum ?? ''}:${vertical ?? ''}:${siteMode ?? ''}`}
        blocks={blocks}
        locale={locale}
        themeId={themeId}
        colorsetId={colorsetId}
        spacingEnum={spacingEnum}
        cornerEnum={cornerEnum}
        typographyEnum={typographyEnum}
        editable={editable}
        activeBlockId={activeBlockId}
        activeEditTarget={activeEditTarget}
        onBlockSelect={onBlockSelect}
        pages={pages}
        defaultLocale={defaultLocale}
        supportedLocales={supportedLocales}
        siteMode={siteMode}
        vertical={vertical}
        pathname={pathname}
      />
    </div>
  );
});

export function RegistryOnlyPlaceholder({
  registryKey,
  themeId,
  colorsetId,
}: {
  registryKey: string;
  themeId: string;
  colorsetId: string;
}) {
  return (
    <div className="lab-placeholder">
      <span className="lab-badge lab-badge--warn">registry_only</span>
      <p>
        <strong>{registryKey}</strong> is listed in the platform matrix but has no React
        implementation in <code>@web-studio/core-components</code> yet.
      </p>
      <p className="text-xs">
        Theme: {themeId} · Colorset: {colorsetId}
      </p>
    </div>
  );
}
